import React, { useState, useEffect, useCallback } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/Button'
import { Eye, EyeOff, AlertTriangle, CheckCircle } from 'lucide-react'
import type { Category } from '@/types/database'

export const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const supabase = createClientComponentClient()

  // Fetch all categories (including hidden ones)
  const fetchCategories = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name')

      if (error) throw error
      console.log('Fetched categories:', data)
      setCategories((data || []) as Category[])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories')
    } finally {
      setLoading(false)
    }
  }, [supabase])

  // Toggle category visibility
  const toggleCategoryVisibility = async (categoryId: string, currentVisibility: boolean) => {
    setUpdating(categoryId)
    setError(null)
    setSuccess(null)

    try {
      console.log(`Updating category ${categoryId} visibility from ${currentVisibility} to ${!currentVisibility}`)
      
      const { data, error } = await supabase
        .from('categories')
        .update({ is_visible: !currentVisibility })
        .eq('id', categoryId)
        .select()

      if (error) throw error

      // Verify the update was successful
      if (!data || data.length === 0) {
        throw new Error('No category was updated')
      }

      console.log('Category update successful:', data[0])

      // Refresh the categories list to ensure we have the latest data
      await fetchCategories()

      setSuccess(
        !currentVisibility 
          ? 'Category is now visible to public users' 
          : 'Category is now hidden from public users'
      )
    } catch (err) {
      console.error('Error updating category visibility:', err)
      setError(err instanceof Error ? err.message : 'Failed to update category')
      
      // Refresh the categories list to ensure we have the correct state
      await fetchCategories()
    } finally {
      setUpdating(null)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Category Management</h2>
        <p className="text-gray-600 mb-6">
          Manage category visibility for troubleshooting. Hidden categories are only accessible to admins.
        </p>

        {success && (
          <div className="bg-green-100 border border-green-200 text-green-800 px-4 py-3 rounded-md relative mb-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              {success}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-md relative mb-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => (
            <div 
              key={category.id} 
              className={`p-4 border rounded-lg ${!category.is_visible ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-lg">
                    {category.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{category.name}</h4>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${category.is_visible ? 'text-green-600' : 'text-red-600'}`}>
                  {category.is_visible ? 'Visible' : 'Hidden'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleCategoryVisibility(category.id, category.is_visible)}
                  disabled={updating === category.id}
                  className={`${category.is_visible ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                >
                  {updating === category.id ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : category.is_visible ? (
                    <><EyeOff className="w-4 h-4 mr-2" /> Hide</>
                  ) : (
                    <><Eye className="w-4 h-4 mr-2" /> Show</>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
